import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'
import Earnings from './Earnings.jsx'

const money = (value) => `${value.toFixed(value < 10 ? 2 : 0)} ден`

/** A bar drawn with a div. Enough to see which row dominates, and no library to keep alive. */
function Bar({ value, max }) {
  return (
    <span className="bar">
      <span className="bar__fill" style={{ width: `${max > 0 ? Math.round((value / max) * 100) : 0}%` }} />
    </span>
  )
}

export default function Costs({ onExpired }) {
  const { t } = useTranslation()
  const [days, setDays] = useState(30)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    api
      .costs(days)
      .then((result) => {
        setData(result)
        setError(null)
      })
      .catch((failure) => {
        onExpired(failure)
        setError(failure.message)
      })
  }, [days, onExpired])

  if (!data) return <p className="muted center">{error ?? t('common.loading')}</p>

  const maxPurpose = Math.max(...data.byPurpose.map((row) => row.mkd), 0)
  const maxDay = Math.max(...data.byDay.map((row) => row.mkd), 0)
  const maxTenant = Math.max(...data.byTenant.map((row) => row.costMkd), 0)

  return (
    <>
      <div className="tabsrow">
        {[7, 30, 90, 365].map((option) => (
          <button
            key={option}
            type="button"
            className={`chip ${days === option ? 'on' : ''}`}
            onClick={() => setDays(option)}
          >
            {t('dashboard.days', { count: option })}
          </button>
        ))}
      </div>

      {error && <p className="status bad">{error}</p>}

      <section className="card">
        <h2>{t('costs.title')}</h2>
        <div className="stats">
          <div className="stat">
            <div className="stat__value">{money(data.totalMkd)}</div>
            <div className="stat__label">{t('costs.total')}</div>
          </div>
          <div className="stat">
            <div className="stat__value">{money(data.perPublishedPostMkd)}</div>
            <div className="stat__label">{t('costs.perPost')}</div>
          </div>
          <div className="stat">
            <div className="stat__value">{data.publishedInWindow}</div>
            <div className="stat__label">{t('costs.publishedInWindow')}</div>
          </div>
        </div>
        <p className="muted small">
          {t('costs.assumptions', {
            rate: data.rate,
            vat: Math.round(data.vatRate * 100),
            fee: Math.round(data.paymentFeeRate * 100),
          })}
        </p>
      </section>

      <Earnings earnings={data.earnings} days={days} />

      <section className="card">
        <h2>{t('costs.byPurpose')}</h2>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>{t('costs.purpose')}</th>
                <th className="num">{t('costs.calls')}</th>
                <th className="num">{t('costs.perCall')}</th>
                <th className="num">{t('costs.sum')}</th>
                <th className="wide" />
              </tr>
            </thead>
            <tbody>
              {data.byPurpose.map((row) => (
                <tr key={row.purpose}>
                  <td>
                    {row.purpose}
                    {row.failures > 0 && <span className="bad small"> · {t('costs.failures', { count: row.failures })}</span>}
                  </td>
                  <td className="num">{row.calls}</td>
                  <td className="num">{row.perCallMkd.toFixed(3)}</td>
                  <td className="num">{money(row.mkd)}</td>
                  <td className="wide">
                    <Bar value={row.mkd} max={maxPurpose} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <h2>{t('costs.byTenant')}</h2>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>{t('costs.tenant')}</th>
                <th className="num">{t('costs.published')}</th>
                <th className="num">{t('costs.cost')}</th>
                <th className="num">{t('costs.paid')}</th>
                <th className="wide" />
              </tr>
            </thead>
            <tbody>
              {data.byTenant.map((row) => (
                <tr key={row.tenantId}>
                  <td>
                    <a href={`#/tenant/${row.tenantId}`}>{row.name}</a>
                  </td>
                  <td className="num">{row.published}</td>
                  <td className="num">{money(row.costMkd)}</td>
                  <td className={`num ${row.paidMkd === 0 && row.costMkd > 50 ? 'bad' : ''}`}>{money(row.paidMkd)}</td>
                  <td className="wide">
                    <Bar value={row.costMkd} max={maxTenant} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="muted small">{t('costs.tenantHint')}</p>
      </section>

      <section className="card">
        <h2>{t('costs.byDay')}</h2>
        <div className="days">
          {data.byDay.map((row) => (
            <div key={row.day} className="day">
              <span className="day__bar" style={{ height: `${maxDay > 0 ? Math.max(2, (row.mkd / maxDay) * 100) : 2}%` }} />
              <span className="day__label">{row.day.slice(5)}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="card">
        <h2>{t('costs.byModel')}</h2>
        {data.byModel.map((row) => (
          <p key={row.model} className="row small">
            <span>{row.model}</span>
            <span>
              {row.calls} · {money(row.mkd)}
            </span>
          </p>
        ))}
      </section>
    </>
  )
}
